// src/components/workflows/WorkflowKanbanView.jsx - Full-Screen Kanban Board (View Mode)
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { Clock } from 'lucide-react';
import useWorkflowStore from '../../stores/WorkflowStore';

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Extract initials from name (max 2 letters)
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Generate consistent color class from name
 */
const getAvatarColor = (name) => {
  if (!name) return 'bg-zinc-500';

  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-emerald-500',
  ];

  // Simple hash function
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/**
 * Format relative time from ISO string
 */
const getRelativeTime = (isoString) => {
  if (!isoString) return null;

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

/**
 * Get friendly status label and color
 */
const getStatusInfo = (status, waitingFor) => {
  if (status === 'waiting_for_event' && waitingFor) {
    const eventLabels = {
      'appointment_completed': 'Waiting for appointment',
      'invoice_paid': 'Waiting for payment',
      'customer_updated': 'Waiting for update',
    };
    return {
      label: eventLabels[waitingFor] || 'Waiting',
      color: 'amber',
      dot: true,
    };
  }

  const statusMap = {
    'scheduled': { label: 'Scheduled', color: 'blue', dot: true },
    'in_progress': { label: 'In progress', color: 'green', dot: true, pulse: true },
    'idle': null,
  };

  return statusMap[status] || null;
};

/**
 * Format phone number
 */
const formatPhone = (phone) => {
  if (!phone) return null;
  // Simple US format: (555) 123-4567
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone; // Return as-is if not 10 digits
};

// ================================
// CUSTOMER CARD COMPONENT
// ================================

/**
 * CustomerCard - Individual customer card within a stage
 */
const CustomerCard = ({ customer }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: customer.id,
    data: {
      type: 'customer',
      customer,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  const initials = getInitials(customer.name);
  const avatarColor = getAvatarColor(customer.name);
  const relativeTime = getRelativeTime(customer.entered_node_at);
  const statusInfo = getStatusInfo(customer.status, customer.waiting_for);
  const formattedPhone = formatPhone(customer.phone);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="
        group
        bg-zinc-900
        border border-zinc-700/50
        rounded-lg
        p-3.5
        mb-2.5
        cursor-grab
        active:cursor-grabbing
        transition-all duration-200
        shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]
        hover:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)]
        hover:border-zinc-500
        hover:-translate-y-0.5
      "
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`
          ${avatarColor}
          w-9 h-9
          rounded-full
          flex items-center justify-center
          flex-shrink-0
          text-white text-sm font-semibold
          shadow-sm
        `}>
          {initials}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name & Status */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-zinc-100 truncate">
              {customer.name}
            </div>
            {statusInfo && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className={`
                  w-1.5 h-1.5
                  rounded-full
                  bg-${statusInfo.color}-500
                  ${statusInfo.pulse ? 'animate-pulse' : ''}
                `} />
              </div>
            )}
          </div>

          {/* Email */}
          <div className="text-xs text-zinc-400 truncate mb-1">
            {customer.email}
          </div>

          {/* Phone (if available) */}
          {formattedPhone && (
            <div className="text-xs text-zinc-500 truncate mb-1">
              {formattedPhone}
            </div>
          )}

          {/* Metadata row */}
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-zinc-700/30">
            {relativeTime && (
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Clock className="w-3 h-3" />
                {relativeTime}
              </div>
            )}
            {statusInfo && (
              <div className={`text-xs text-${statusInfo.color}-400`}>
                {statusInfo.label}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================
// STAGE COLUMN COMPONENT
// ================================

/**
 * StageColumn - A single stage/column of the kanban board
 */
const StageColumn = ({ stage, customers }) => {
  // Make column a droppable zone
  const { setNodeRef, isOver } = useDroppable({
    id: stage.node_id,
    data: {
      type: 'stage',
      stage,
    },
  });

  // Get border color based on stage color
  const borderColorMap = {
    purple: 'border-purple-500/50',
    green: 'border-green-500/50',
    blue: 'border-blue-500/50',
    orange: 'border-orange-500/50',
    red: 'border-red-500/50',
    gray: 'border-zinc-500/50',
  };

  const borderColor = borderColorMap[stage.color] || borderColorMap.gray;

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-shrink-0
        w-72
        rounded-lg
        border-l-2
        ${isOver ? 'border-l-4 border-purple-500 bg-purple-500/10' : `${borderColor} bg-transparent`}
        transition-all duration-200
      `}
    >
      {/* Column Header - Clean Notion style */}
      <div className="px-4 py-3 border-b border-zinc-700/30">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-200">
            {stage.name}
          </h3>
          <div className="bg-zinc-700/50 text-zinc-400 px-2 py-0.5 rounded-full text-xs font-medium">
            {customers.length}
          </div>
        </div>
      </div>

      {/* Column Content - Customer Cards */}
      <div className="p-3 min-h-[400px] max-h-[calc(100vh-300px)] overflow-y-auto scrollbar-custom">
        <SortableContext
          items={customers.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {customers.map((customer) => (
            <CustomerCard key={customer.id} customer={customer} />
          ))}
        </SortableContext>

        {customers.length === 0 && (
          <div className="text-sm text-zinc-500 italic text-center py-12">
            No customers in this stage
          </div>
        )}
      </div>
    </div>
  );
};

// ================================
// MAIN KANBAN VIEW COMPONENT
// ================================

/**
 * WorkflowKanbanView - Full-screen kanban board for viewing/operating workflow
 *
 * This is the "View Mode" - focused on operations, not design.
 * - Read-only stage structure (can't add/remove stages)
 * - Drag customers between stages
 * - Real-time updates
 * - Modern, minimal, Notion-inspired design with noir gradient
 */
const WorkflowKanbanView = ({ workflowId }) => {
  const [activeCustomer, setActiveCustomer] = useState(null);

  const { workflowBoard, loadWorkflowBoard, moveCustomerToNode } = useWorkflowStore();

  // Load workflow data
  useEffect(() => {
    if (workflowId) {
      loadWorkflowBoard(workflowId);
    }
  }, [workflowId, loadWorkflowBoard]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    })
  );

  // Handle customer drag start
  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'customer') {
      setActiveCustomer(active.data.current.customer);
    }
  };

  // Handle customer drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Handle drop on stage column (stage is really a node with isStageGate)
    if (over && active.data.current?.type === 'customer' && over.data.current?.type === 'stage') {
      const activeCustomer = active.data.current.customer;
      const overStage = over.data.current.stage;

      if (activeCustomer.current_node_id !== overStage.node_id) {
        // Move customer to new node
        moveCustomerToNode(activeCustomer.id, overStage.node_id);
      }
    }

    setActiveCustomer(null);
  };

  return (
    <div className="w-full h-full bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100% p-6 overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto h-full pb-4 scrollbar-custom">
          {workflowBoard?.stages?.map((stage) => (
            <StageColumn
              key={stage.node_id}
              stage={stage}
              customers={stage.items || []}
            />
          ))}

          {(!workflowBoard?.stages || workflowBoard.stages.length === 0) && (
            <div className="flex items-center justify-center w-full h-full">
              <div className="text-center">
                <div className="text-2xl text-zinc-400 mb-2">No stage gates yet</div>
                <div className="text-sm text-zinc-500">Switch to edit mode and mark nodes as stage gates</div>
              </div>
            </div>
          )}
        </div>

        {/* Drag Overlay - Shows dragged customer */}
        {typeof window !== 'undefined' && createPortal(
          <DragOverlay
            dropAnimation={null}
            modifiers={[snapCenterToCursor]}
          >
            {activeCustomer && (
              <div className="
                w-72
                bg-zinc-800/95
                backdrop-blur-sm
                border-2 border-purple-500
                rounded-lg
                shadow-2xl
                p-3.5
              ">
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className={`
                    ${getAvatarColor(activeCustomer.name)}
                    w-10 h-10
                    rounded-full
                    flex items-center justify-center
                    flex-shrink-0
                    text-white text-sm font-semibold
                    shadow-lg
                  `}>
                    {getInitials(activeCustomer.name)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-100 truncate mb-1">
                      {activeCustomer.name}
                    </div>
                    {activeCustomer.email && (
                      <div className="text-xs text-zinc-300 truncate">
                        {activeCustomer.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );
};

export default WorkflowKanbanView;
